// Call controls inside the call pill (Transfer / End). Renders nothing unless this
// user can call from the CRM. The desk itself is changed from the pill's menu
// (CU-869fmcc9r). No Hold: Elevate offers no hold command (CU-869fn06p7). With none
// of the handsets this user could act through signed in, the buttons give way to a
// short reason instead of failing silently (CU-869fn0jee).
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Box, Button, Divider, ListSubheader, Menu, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import Iconify from '../Iconify';
import { useOutbound } from './OutboundCallProvider';
import { CONTROL_LABEL, controlReadiness, controlsFor, deskLabel, myPhoneTargets, transferTargets } from './outboundModel';

const ICON = { transfer: 'mdi:phone-forward', end: 'mdi:phone-hangup' };

const pillBtnSx = (danger) => ({
    minWidth: 0,
    px: 1,
    borderRadius: 999,
    flexShrink: 0,
    color: 'common.white',
    bgcolor: danger ? 'error.main' : 'rgba(255,255,255,0.12)',
    '&:hover': { bgcolor: danger ? 'error.dark' : 'rgba(255,255,255,0.24)' },
});

export default function PillControls({ call }) {
    const outbound = useOutbound();
    const [anchor, setAnchor] = useState(null);
    const [outside, setOutside] = useState(null); // null = hidden, else the typed number
    const close = () => { setAnchor(null); setOutside(null); };
    if (!outbound?.canCall || !call) return null;
    const controls = controlsFor(call);
    // Only on this user's own calls: other people's pills stay uncluttered.
    if (!call.mine || call.recipientReason === 'superuser') return null;
    if (controls.length === 0) return null;
    const readiness = controlReadiness(outbound.desks, outbound.myDesk?.id ?? null);
    if (!readiness.ready) {
        return (
            <Tooltip title={readiness.reason}>
                <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    role="note"
                    aria-label={readiness.reason}
                    data-testid="pill-controls-blocked"
                    sx={{ px: 1, flexShrink: 0, color: 'rgba(255,255,255,0.8)' }}
                >
                    <Iconify icon="mdi:information-outline" width={16} />
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>Can&apos;t transfer from here</Typography>
                </Stack>
            </Tooltip>
        );
    }
    const targets = transferTargets(outbound.desks, outbound.deskId);
    // The server's claim, not this browser's remembered one: the backend decides from
    // presence whether you hold a desk, and that is what it will check.
    const myPhone = myPhoneTargets(outbound.desks, outbound.myDesk?.id ?? null);
    return (
        <Stack direction="row" spacing={0.5} alignItems="center" data-testid="pill-controls">
            {controls.map((action) => (action === 'transfer' ? (
                <Button
                    key={action}
                    size="small"
                    onClick={(e) => setAnchor(e.currentTarget)}
                    aria-label={CONTROL_LABEL[action]}
                    aria-haspopup="menu"
                    aria-expanded={Boolean(anchor)}
                    data-testid="pill-control-transfer"
                    sx={pillBtnSx(false)}
                >
                    <Iconify icon={ICON[action]} width={16} />
                </Button>
            ) : (
                <Tooltip key={action} title={CONTROL_LABEL[action]}>
                    <Button
                        size="small"
                        onClick={() => outbound.control(call, action)}
                        aria-label={CONTROL_LABEL[action]}
                        data-testid={`pill-control-${action}`}
                        sx={pillBtnSx(action === 'end')}
                    >
                        <Iconify icon={ICON[action]} width={16} />
                    </Button>
                </Tooltip>
            )))}
            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
                {myPhone.length > 0 && <ListSubheader sx={{ lineHeight: 2 }}>Send it to the phone you are at, and remember it</ListSubheader>}
                {myPhone.map((d) => (
                    <MenuItem
                        key={`mine-${d.id}`}
                        onClick={() => { close(); outbound.control(call, 'transfer', { desk_id: d.id, claim_target: true }); }}
                        data-testid={`transfer-to-me-${d.id}`}
                    >
                        I am at {deskLabel(d)}
                    </MenuItem>
                ))}
                {myPhone.length > 0 && targets.length > 0 && <Divider />}
                {targets.length > 0 && <ListSubheader sx={{ lineHeight: 2 }}>Or just send it to another phone</ListSubheader>}
                {targets.map((d) => (
                    <MenuItem
                        key={d.id}
                        onClick={() => { close(); outbound.control(call, 'transfer', { desk_id: d.id }); }}
                        data-testid={`transfer-to-${d.id}`}
                    >
                        Transfer to {deskLabel(d)}{d.held_by?.name ? ` (${d.held_by.name})` : ''}
                    </MenuItem>
                ))}
                <Divider />
                {outside === null ? (
                    <MenuItem onClick={() => setOutside('')} data-testid="transfer-to-number">
                        A mobile or outside number…
                    </MenuItem>
                ) : (
                    <Box
                        sx={{ px: 2, py: 1 }}
                        onKeyDown={(e) => {
                            // Keep typed characters away from the menu's typeahead, but
                            // let Escape close it and Enter send.
                            if (e.key === 'Escape') return;
                            e.stopPropagation();
                            if (e.key === 'Enter' && outside.trim().length >= 6) {
                                const number = outside.trim();
                                close();
                                outbound.control(call, 'transfer', { number });
                            }
                        }}
                    >
                        <TextField
                            size="small"
                            autoFocus
                            fullWidth
                            placeholder="07700 900123"
                            value={outside}
                            onChange={(e) => setOutside(e.target.value)}
                            inputProps={{ 'aria-label': 'Number to transfer to', 'data-testid': 'transfer-number-input' }}
                        />
                        <Button
                            size="small"
                            variant="contained"
                            fullWidth
                            sx={{ mt: 1 }}
                            disabled={outside.trim().length < 6}
                            onClick={() => { const number = outside.trim(); close(); outbound.control(call, 'transfer', { number }); }}
                            data-testid="transfer-number-send"
                        >
                            Send the call there
                        </Button>
                    </Box>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1, maxWidth: 280 }}>
                    There is no &ldquo;take the call&rdquo; button because the phone system does not offer one. Sending
                    it to your own phone is how you take it.
                </Typography>
            </Menu>
        </Stack>
    );
}

PillControls.propTypes = { call: PropTypes.object };
